import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * useApiList - fetches a list from an endpoint with automatic retry on failure,
 * silent background refresh on window focus, and a loading/error state.
 *
 * Returns: { data, loading, error, refetch }
 *
 * Why: production endpoints may transiently return 5xx or empty during
 * cold-starts / seed propagation. A short retry loop with backoff masks these
 * blips so the public pages always show the catalogue.
 */
export default function useApiList(path, { params, retries = 3, retryDelay = 800, autoRefresh = true } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    attemptRef.current = 0;
    while (attemptRef.current <= retries) {
      try {
        const { data: resp } = await api.get(path, { params });
        if (!mountedRef.current) return;
        setData(Array.isArray(resp) ? resp : []);
        setLoading(false);
        setError(null);
        return;
      } catch (e) {
        attemptRef.current += 1;
        if (attemptRef.current > retries) {
          if (!mountedRef.current) return;
          setError(e);
          setLoading(false);
          return;
        }
        // Exponential-ish backoff: 800ms, 1600ms, 3200ms
        await new Promise((r) => setTimeout(r, retryDelay * attemptRef.current));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(params || {}), retries, retryDelay]);

  useEffect(() => {
    mountedRef.current = true;
    load(false);
    return () => { mountedRef.current = false; };
  }, [load]);

  // Silent background refresh when the tab regains focus - if the user
  // leaves the tab open and admin publishes a new course, they see it on return.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const onFocus = () => load(true);
    const onVisible = () => { if (document.visibilityState === "visible") load(true); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [autoRefresh, load]);

  return { data, loading, error, refetch: () => load(false), setData };
}
